import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { map, switchMap } from 'rxjs/operators';

import { AccountService } from 'app/core';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project, Property } from 'app/modules/projects/projects.types';
import { ProjectType, PropertyType, ProjectStage } from 'app/core/models/account.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { isEmptyOrWhitespace } from 'app/shared/Utils/common.utils';
import { untilComponentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';

@Component({
  selector: 'project-add-address',
  templateUrl: './project-add-address.component.html',
  styleUrls: ['./project-add-address.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectAddAddressComponent extends OnDestroyMixin implements OnInit {
  addPropertyForm: UntypedFormGroup;

  projectTypes: ProjectType[];
  propertyTypes: PropertyType[];
  projectStages: ProjectStage[];
  property: Property;
  project: Project;

  duplicatePropertyId: number = 0;
  duplicateProperties: Project[] = [];
  duplicateAddressProperties: Project[] = [];
  public propertyAlreadyChild: boolean;
  public activeButtonIndex: number;
  propertiesFoundByName = [];

  private similarPropertiesFieldsQuery = ['id', 'name', 'address_string'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<ProjectAddAddressComponent>,
    private _formBuilder: UntypedFormBuilder,
    private _changeDetectorRef: ChangeDetectorRef,
    private _projectsService: ProjectsService,
    private _propertiesService: PropertiesService,
    private _accountService: AccountService,
    private _router: Router,
  ) {
    super();
    this._propertiesService.accountId = this._accountService.getCurrentAccount().id;
  }

  ngOnInit(): void {
    this.addPropertyForm = this._formBuilder.group({
      property_type_id: [''],
      type: [''],
      name: '',
      address: this._formBuilder.group({
        address_string: '',
        street: '',
        city: ['', Validators.required],
        state: '',
        postal_code: '',
        latitude: '',
        longitude: '',
      }),
    });

    this.propertyTypes = this.data.data.options.property_types.filter((option) => !option.hidden);

    if (this.data.property) {
      this.property = this.data.property;
      this.addPropertyForm.patchValue(this.property);
    }

    if (this.data.project) {
      this.project = this.data.project;
    }
  }

  setAddressParts(addressParts) {
    console.log(addressParts);
    this.addPropertyForm
      .get('address')
      .get('city')
      .setValue(addressParts.locality || addressParts.sublocality_level_1);
    this.addPropertyForm.get('address').get('state').setValue(addressParts.administrative_area_level_1);
    this.addPropertyForm.get('address').get('postal_code').setValue(addressParts.postal_code);
    if (addressParts.street_number && addressParts.route) {
      this.addPropertyForm
        .get('address')
        .get('street')
        .setValue(addressParts.street_number + ' ' + addressParts.route);
    } else {
      this.addPropertyForm.get('address').get('street').setValue('');
    }
    this.addPropertyForm.get('address').get('latitude').setValue(addressParts.lat);
    this.addPropertyForm.get('address').get('longitude').setValue(addressParts.lng);

    this.addressBlur();
    this._changeDetectorRef.markForCheck();
  }

  addressBlur() {
    this.duplicatePropertyId = 0;
    let address = this.addPropertyForm.get('address').value;
    if (address.street !== '' && address.city !== '' && address.state !== '' && address.postal_code !== '') {
      let queryFilters = {
        address: address.street,
        city: address.city,
        state: address.state,
        zip: address.postal_code,
      };

      let queryFiltersLocation = {
        location: {},
      };

      let addressString = `${queryFilters['address']} ${queryFilters['city']}, ${queryFilters['state']} ${queryFilters['zip']}`;

      queryFiltersLocation.location = { addressString: addressString, within: '0.1mi' };

      this._propertiesService
        .getPropertiesWithoutSort(this.similarPropertiesFieldsQuery, queryFiltersLocation, 0, 20)
        .pipe(untilComponentDestroyed(this))
        .subscribe((data) => {
          if (data) {
            if (data.properties) {
              let existingPropertyIds = this.project?.properties.map((property) => Number(property.id));
              this.duplicateAddressProperties = data.properties?.filter(
                (property) => !existingPropertyIds.includes(Number(property.id)),
              );
              //this._isPropertyAlreadyChildren(this.project.properties, this.duplicateAddressProperties);

              this._changeDetectorRef.markForCheck();
              this._changeDetectorRef.detectChanges();
            }
          }
        });
    }
  }

  /**
   * @param childrenDealsArray 'children' array property returned from MAT_DIALOG_DATA project object.
   * @param addressArray the 'address_string' property value returned from duplicatedAddressProjects array.
   * @returns boolean value which identifies if child deal already exists as child portfolio.
   */
  private _isPropertyAlreadyChildren(childrenPropertiesArray: Array<any>, addressArray?: Array<any>): boolean {
    if (addressArray && addressArray.length > 0) {
      return (this.propertyAlreadyChild = childrenPropertiesArray.some(
        (el) => el.address_string === addressArray[0]['address_string'],
      ));
    } else {
      return (this.propertyAlreadyChild = false);
    }
  }

  goToProperty(property): void {
    this._router.navigateByUrl('/properties/' + property.id);
    this.discard();
  }

  discard(): void {
    this.matDialogRef.close();
  }

  addPropertyToProject(property: Property, index: number) {
    this.activeButtonIndex = index;
    this._projectsService
      .relatePropertyToProject(property)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.discard());
  }

  save(): void {
    this.addPropertyForm.disable();

    if (this.property) {
      let property = { ...this.property, ...this.addPropertyForm.value };
      //clear out the address_string as the address will not be updated.
      property.address.address_string = '';
      this._projectsService
        .updateProperty(property)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => this.matDialogRef.close());
    } else {
      let property = this.addPropertyForm.value;
      this._projectsService
        .addProperty(property)
        .pipe(
          untilComponentDestroyed(this),
          switchMap(() => this._projectsService.getProjectById(this.project.id)),
        )
        .subscribe(() => this.matDialogRef.close());
    }
  }

  nameBlur(): void {
    const name = this.addPropertyForm.get('name').value;

    this.propertiesFoundByName = [];

    if (isEmptyOrWhitespace(name)) return;

    const queryFilters = { fjson: `[["name","contains","${name}"]]`, q: '' };

    this._propertiesService
      .getPropertiesWithoutSort(this.similarPropertiesFieldsQuery, queryFilters, 0, 10)
      .pipe(
        untilComponentDestroyed(this),
        map(({ properties }: { properties: Property[] }) => properties),
      )
      .subscribe((properties) => (this.propertiesFoundByName = properties));
  }
}
